/**
 * What each declared field MEANS: its operator-facing label, its input type, and where its value
 * lives on the row.
 *
 * Labels are seedtrac's, verbatim, down to the abbreviations — "NoB", "Moist %", "PP", "SPC-F",
 * "Prod. Loc.". An operator moving off seedtrac should not have to translate.
 *
 * This lives in shared so the grid, the API validation and the reports all read one definition; a
 * column that a form declares but nothing here describes is a bug, and the tests assert it cannot
 * happen.
 */
import {
  GOT_TYPE_LABELS,
  PRODUCTION_GRADES,
  QUALITY_VERDICT_LABELS,
} from "../quality";
import { LOT_QC_STATUSES, LOT_QC_STATUS_CODES } from "../qc";
import type {
  HeaderFieldKey,
  LineColumnKey,
  TransportFieldKey,
  TransportMode,
} from "./types";

export type FieldInput =
  | "text"
  | "number"
  | "decimal"
  | "percent"
  | "date"
  | "datetime"
  | "crop"
  | "variety"
  | "stage"
  | "vendor"
  | "facility"
  | "select"
  | "checkbox"
  | "readonly";

export interface FieldMeta {
  /** The grid header, seedtrac's abbreviation: "Prod. Per.", "NoB", "Moist %". */
  label: string;
  /**
   * The post item form's label, spelled out: "Production Personnel", "No. of Bags (NoB)". One key,
   * two surfaces, one metadata row. Falls back to `label`.
   */
  formLabel?: string;
  input: FieldInput;
  /** Right-align in the grid and the PDF. */
  num?: boolean;
  /** Operator may type into it while the document is still open. */
  editable?: boolean;
  /** Fixed choices, for `select` inputs. */
  options?: readonly { value: string; label: string }[];
  /** Column width hint in ch, for the grid and the xlsx. */
  width?: number;
  /**
   * Render-time formatting for a COMPUTED read-only value: "14 h 20 m", "2.06 %".
   *
   * Deliberately not a `FieldInput`. `input` answers "which control does the operator type into";
   * a derived value has no control, only a presentation, and conflating the two would put
   * "duration" in a union that `field-input.tsx` switches over to build editors.
   */
  format?: "duration" | "percent";
}

const GOT_TYPE = (["required", "not_required"] as const).map((v) => ({
  value: v,
  label: GOT_TYPE_LABELS[v],
}));
/** `lots.qc_status`, as the grid prints it. Read-only: the QA gate owns the value, not this form. */
const QC_STATUS = LOT_QC_STATUSES.map((v) => ({
  value: v,
  label: LOT_QC_STATUS_CODES[v],
}));
// Two choices only (user, 2026-09-29): an unset verdict shows the select's own --Select--.
const VERDICT = [
  { value: "acceptable", label: QUALITY_VERDICT_LABELS.acceptable },
  { value: "not_acceptable", label: QUALITY_VERDICT_LABELS.not_acceptable },
] as const;
/** seedtrac offers whole years of shelf life; 36 months is its default. */
const LE_MONTHS = [12, 24, 36, 48, 60].map((m) => ({
  value: String(m),
  label: String(m),
}));
// No blank choice (user, 2026-10-15): "N/A" is one of the grades.
const PRODUCTION_GRADE = PRODUCTION_GRADES.map((g) => ({ value: g, label: g }));
const SEED_STATUS = [
  { value: "", label: "N/A" },
  { value: "drying", label: "Drying" },
  { value: "fumigation", label: "Fumigation" },
  { value: "quarantine", label: "Quarantine" },
] as const;

export const LINE_COLUMN_META: Record<LineColumnKey, FieldMeta> = {
  crop: { label: "Crop", input: "crop", editable: true, width: 16 },
  variety: { label: "Variety", input: "variety", editable: true, width: 18 },
  spc_f: {
    label: "SPC-F",
    formLabel: "SP Code Female",
    input: "text",
    editable: true,
    width: 10,
  },
  spc_m: {
    label: "SPC-M",
    formLabel: "SP Code Male",
    input: "text",
    editable: true,
    width: 10,
  },
  sp_pair: {
    label: "SP Code (F X M)",
    input: "text",
    editable: false,
    width: 16,
  },
  vendor_lot_no: {
    label: "V. Lot No.",
    input: "text",
    editable: true,
    width: 14,
  },
  lot_number: { label: "Lot No.", input: "readonly", width: 22 },
  stage: { label: "Stage", input: "stage", editable: true, width: 12 },
  declared_qty: { label: "Qty", input: "decimal", num: true, editable: true },
  declared_bags: { label: "NoB", input: "number", num: true, editable: true },
  // seedtrac types Actual on the post item form (p66) rather than deriving it. On a bag-wise form the
  // scale writes the same pair, and `submitArrival` reconciles the two — see ARRIVAL_SPEC.
  received_qty: { label: "Qty", input: "decimal", num: true, editable: true },
  received_bags: { label: "NoB", input: "number", num: true, editable: true },
  difference: { label: "Qty", input: "readonly", num: true },
  difference_bags: { label: "NoB", input: "readonly", num: true },

  storage: {
    label: "SLOC",
    formLabel: "Storage Location (SLOC)",
    input: "readonly",
    width: 24,
  },

  // Preliminary QC — arrival-time observation, never a QA result.
  moist_pct: { label: "Moist %", input: "percent", num: true, editable: true },
  pp: { label: "PP", input: "percent", num: true, editable: true },
  // The post item form's verdicts. Deliberately NOT a relabelling of the two above — see quality.ts.
  moisture_status: {
    label: "Moisture",
    input: "select",
    options: VERDICT,
    editable: true,
    width: 14,
  },
  pp_status: {
    label: "Physical Purity",
    input: "select",
    options: VERDICT,
    editable: true,
    width: 16,
  },
  germ_pct: { label: "Germ %", input: "percent", num: true, editable: true },
  got: { label: "GOT", input: "select", options: GOT_TYPE, editable: true },
  got_type: {
    label: "GOT Type",
    input: "select",
    options: GOT_TYPE,
    editable: true,
  },
  qc_status: {
    label: "QC Status",
    input: "readonly",
    width: 12,
    options: QC_STATUS,
  },
  // The lot's QC status as its code (UT, OK, …), as the QC Status column prints it.
  quality_status: {
    label: "Quality Status",
    input: "readonly",
    width: 14,
    options: QC_STATUS,
  },
  seed_status: {
    label: "Seed Status",
    input: "select",
    options: SEED_STATUS,
    editable: true,
    width: 14,
  },

  // Production provenance — printed on the PHSRN and the FRN.
  organiser: { label: "Organiser", input: "vendor", editable: true, width: 18 },
  farmer: { label: "Farmer", input: "text", editable: true, width: 16 },
  plot_no: { label: "Plot No.", input: "text", editable: true, width: 12 },
  production_location: {
    label: "Prod. Loc.",
    formLabel: "Production Location",
    input: "text",
    editable: true,
    width: 16,
  },
  production_person: {
    label: "Prod. Per.",
    formLabel: "Production Personnel",
    input: "text",
    editable: true,
    width: 16,
  },
  production_grade: {
    // seedtrac's grid spells this one out rather than abbreviating it (p66).
    label: "Production Grade",
    input: "select",
    options: PRODUCTION_GRADE,
    editable: true,
    width: 12,
  },

  // Post item form only (p66).
  pdn_no: { label: "PDN No.", input: "readonly", width: 14 },
  pdn_date: { label: "PDN Date", input: "readonly", width: 12 },
  harvest_date: { label: "Harvest Date", input: "date", editable: true },
  tare_weight: {
    label: "Tare Wt.",
    formLabel: "Tare Weight",
    input: "decimal",
    num: true,
    editable: true,
  },
  le_months: {
    label: "LE Duration",
    formLabel: "Life Expectancy (LE) Duration",
    input: "select",
    options: LE_MONTHS,
    editable: true,
    width: 12,
  },
  le_date: {
    label: "LE",
    formLabel: "Life Expectancy (LE)",
    input: "readonly",
    width: 12,
  },

  // ---- The cob chain ----------------------------------------------------------------------
  // Labels are seedtrac's Drying Slip header, verbatim (manual p319-320). The qty/bag pairs read
  // "Qty" / "NoB" like every other grid pair and take their meaning from the group spanning them,
  // which is why `lineGroups` on the cob forms is not optional decoration.
  source_lot: { label: "Source Lot", input: "readonly", width: 22 },
  source_sloc: {
    label: "Existing SLOC",
    formLabel: "Existing SLOC",
    input: "readonly",
    width: 24,
  },
  post_drying_qty: {
    label: "Qty",
    formLabel: "Quantity",
    input: "decimal",
    num: true,
    editable: true,
  },
  post_drying_bags: {
    label: "NoB",
    formLabel: "No. of Bags (NoB)",
    input: "number",
    num: true,
    editable: true,
  },
  drying_loss: {
    label: "Qty",
    formLabel: "Drying Loss",
    input: "readonly",
    num: true,
  },
  drying_loss_pct: {
    label: "%",
    formLabel: "Drying Loss %",
    input: "readonly",
    num: true,
    format: "percent",
  },
  drying_start: {
    label: "Drying Start",
    input: "datetime",
    editable: true,
    width: 18,
  },
  drying_end: {
    label: "Drying End",
    input: "datetime",
    editable: true,
    width: 18,
  },
  // seedtrac's own abbreviation for the elapsed time between the two.
  drying_hours: {
    label: "Total D.Time",
    formLabel: "Total Drying Time",
    input: "readonly",
    format: "duration",
    width: 14,
  },
  // Free text, not a reference: there is no activity or dryer master in this schema, and the slip
  // records the machine ("Machine 01") as a note beside the run.
  drying_details: {
    label: "Drying Details",
    input: "text",
    editable: true,
    width: 18,
  },
  available_qty: {
    label: "Qty",
    formLabel: "Available Quantity",
    input: "readonly",
    num: true,
  },
  available_bags: {
    label: "NoB",
    formLabel: "Available (NoB)",
    input: "readonly",
    num: true,
  },
  dispatch_qty: {
    label: "Qty",
    formLabel: "Quantity",
    input: "decimal",
    num: true,
    editable: true,
  },
  dispatch_bags: {
    label: "NoB",
    formLabel: "No. of Bags (NoB)",
    input: "number",
    num: true,
    editable: true,
  },

  actions: { label: "", input: "readonly", width: 8 },
};

export const HEADER_FIELD_META: Record<HeaderFieldKey, FieldMeta> = {
  arrival_date: { label: "Arrival Date", input: "date", editable: true },
  dispatch_date: { label: "Dispatch Date", input: "date", editable: true },
  dc_date: { label: "DC Date", input: "date", editable: true },
  dc_number: { label: "DC No.", input: "text", editable: true },
  party: { label: "Party", input: "vendor", editable: true },
  order_ref_no: { label: "Order Ref No.", input: "text", editable: true },
  address: { label: "Address", input: "text", editable: true },
  source_plant: {
    label: "Stock Transfer from Plant",
    input: "facility",
    editable: true,
  },
  header_crop: { label: "Crop", input: "crop", editable: true },
  header_variety: { label: "Variety", input: "variety", editable: true },
  header_vendor_variety: {
    label: "Vendor Variety",
    input: "text",
    editable: true,
  },
  no_of_lots: {
    label: "No. of Lots",
    input: "number",
    num: true,
    editable: true,
  },
  header_stage: { label: "Stage", input: "stage", editable: true },
  // The weighbridge trio, in the operator's own words. `kg` rides in the field's suffix slot
  // rather than the label, as "Months" and "From Harvest Date" already do.
  loaded_weight: {
    label: "Loaded Truck Weight",
    input: "decimal",
    num: true,
    editable: true,
  },
  empty_weight: {
    label: "Empty Truck Weight",
    input: "decimal",
    num: true,
    editable: true,
  },
  // Derived, never typed and never sent: `weighbridgeNet` computes it on both sides.
  net_weight: { label: "Net Weight", input: "readonly", num: true },
  dest_plant: { label: "Dispatch to Plant", input: "facility", editable: true },
  note_serial_no: { label: "DC No.", input: "text", editable: true },
  camp_at: { label: "Camp at", input: "text", editable: true },
  source_state: { label: "State", input: "text", editable: true },
  lorry_no: {
    label: "Lorry No. / Courier Bill No.",
    input: "text",
    editable: true,
  },
  driver_name: { label: "Driver Name", input: "text", editable: true },
  driver_mobile: { label: "Driver Mobile", input: "text", editable: true },
  from_address: { label: "From Address", input: "text", editable: true },
  to_address: { label: "To Address", input: "readonly" },
  production_person_name: {
    label: "Production Personnel Name",
    input: "text",
    editable: true,
  },
  hybrid_code: { label: "Hybrid Code", input: "text", editable: true },
  recovery_pct: {
    label: "Recovery %",
    input: "readonly",
    num: true,
    format: "percent",
  },
};

/**
 * Header keys that are stored in `documents.extra` rather than in a column of their own. The API
 * accepts an `extra` key only when the form declares it, so a form can never write a field it does
 * not show.
 */
export const HEADER_EXTRA_KEYS = {
  source_plant: "sourceFacilityId",
  address: "sourceAddress",
  order_ref_no: "orderRefNo",
  header_crop: "headerCropId",
  header_variety: "headerVarietyId",
  header_vendor_variety: "headerVendorVariety",
  no_of_lots: "noOfLots",
  header_stage: "headerStageId",
  note_serial_no: "noteSerialNo",
  source_state: "sourceState",
  // The same slot Trading's "Address" writes: one document has one source address.
  from_address: "sourceAddress",
  production_person_name: "productionPersonName",
  hybrid_code: "hybridCode",
} as const satisfies Partial<Record<HeaderFieldKey, string>>;

export type HeaderExtraKey =
  (typeof HEADER_EXTRA_KEYS)[keyof typeof HEADER_EXTRA_KEYS];

/**
 * Header keys whose value lives in `documents.transport`, not in `documents.extra`.
 *
 * `dcDate` / `dcNumber` were already there and already written by the transport block; `dispatchDate`
 * joins them. Without this map `FormHeader.write()` falls through its `HEADER_EXTRA_KEYS` lookup and
 * silently drops all three on every form that declares them.
 */
export const HEADER_TRANSPORT_KEYS = {
  dispatch_date: "dispatchDate",
  dc_date: "dcDate",
  dc_number: "dcNumber",
  camp_at: "dispatchLocation",
  lorry_no: "vehicleNumber",
  driver_name: "driverName",
  driver_mobile: "driverPhone",
} as const satisfies Partial<Record<HeaderFieldKey, string>>;

export type HeaderTransportKey =
  (typeof HEADER_TRANSPORT_KEYS)[keyof typeof HEADER_TRANSPORT_KEYS];

/**
 * Header keys whose value lives in `documents.weighbridge`, the third jsonb column a header field
 * can land in.
 *
 * The column, its zod schema and the `updateHeader` merge all pre-date this map — they were built
 * for a weighbridge nothing ever rendered. Naming the keys here is what finally gives them a
 * screen, and it keeps the "one header key, one home" rule that `HEADER_EXTRA_KEYS` and
 * `HEADER_TRANSPORT_KEYS` already follow.
 *
 * `net_weight` is listed so readers resolve it, but it is COMPUTED on every write: see
 * `weighbridgeNet` in `../arrival`. Nothing should ever send it.
 */
export const HEADER_WEIGHBRIDGE_KEYS = {
  loaded_weight: "loadedKg",
  empty_weight: "emptyKg",
  net_weight: "netKg",
  recovery_pct: "recoveryPct",
} as const satisfies Partial<Record<HeaderFieldKey, string>>;

export type HeaderWeighbridgeKey =
  (typeof HEADER_WEIGHBRIDGE_KEYS)[keyof typeof HEADER_WEIGHBRIDGE_KEYS];

/** "Mode of Transit" renders unconditionally, so it is not a `TransportFieldKey`. */
export const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  transport: "Transport",
  courier: "Courier",
  hand_delivery: "Hand Delivery",
};

/**
 * The arrival screens ask the mode as "Delivery Type" (user, 2026-10-06): Vehicle, Hand Delivery or
 * Courier. Stored values are unchanged (`transport` is Vehicle); lists and printed slips keep
 * seedtrac's "Mode of Transit" wording through `TRANSPORT_MODE_LABELS`.
 */
export const DELIVERY_TYPE_LABEL = "Delivery Type";
export const DELIVERY_TYPE_LABELS: Record<TransportMode, string> = {
  transport: "Vehicle",
  hand_delivery: "Hand Delivery",
  courier: "Courier",
};

const PAYMENT_MODE = [
  { value: "TBB", label: "TBB" },
  { value: "to_pay", label: "To Pay" },
  { value: "paid", label: "Paid" },
] as const;

/**
 * Labels for the transport block, seedtrac's verbatim. The web card hard-coded these ("DC date",
 * "LR number"), which is how they drifted from the words on the operator's old screen.
 */
export const TRANSPORT_FIELD_META: Record<TransportFieldKey, FieldMeta> = {
  transport_name: { label: "Transport Name", input: "text", editable: true },
  lorry_receipt_number: {
    label: "Lorry Receipt No.",
    input: "text",
    editable: true,
  },
  vehicle_number: { label: "Vehicle No.", input: "text", editable: true },
  payment_mode: {
    label: "Payment Mode",
    input: "select",
    options: PAYMENT_MODE,
    editable: true,
  },
  dc_date: { label: "DC Date", input: "date", editable: true },
  dc_number: { label: "DC No.", input: "text", editable: true },
  courier_name: { label: "Courier Name", input: "text", editable: true },
  docket_no: { label: "Docket No.", input: "text", editable: true },
  delivery_person_name: {
    label: "Delivery Person Name",
    input: "text",
    editable: true,
  },
};

/**
 * Which modes show a transport field, where its value lives on `documents.transport`, and whether
 * that mode requires it.
 *
 * This sits beside `missingTransportFields` on purpose: the screen decides what to render from the
 * same table the submit check reads, so a field can never be required by one and hidden by the
 * other.
 */
export const TRANSPORT_FIELD_MODES: Record<
  TransportFieldKey,
  { modes: TransportMode[]; field: string; required?: boolean }
> = {
  transport_name: {
    modes: ["transport"],
    field: "transportName",
    required: true,
  },
  lorry_receipt_number: {
    modes: ["transport"],
    field: "lorryReceiptNumber",
    required: true,
  },
  vehicle_number: {
    modes: ["transport"],
    field: "vehicleNumber",
    required: true,
  },
  payment_mode: { modes: ["transport"], field: "paymentMode", required: true },
  courier_name: { modes: ["courier"], field: "courierName", required: true },
  docket_no: { modes: ["courier"], field: "docketNo", required: true },
  delivery_person_name: {
    modes: ["hand_delivery"],
    field: "deliveryPersonName",
    required: true,
  },
  // Paperwork, not carriage: shown whatever the mode, required only when the form collects DC info.
  dc_date: {
    modes: ["transport", "courier", "hand_delivery"],
    field: "dcDate",
  },
  dc_number: {
    modes: ["transport", "courier", "hand_delivery"],
    field: "dcNumber",
  },
};
